import React from 'react';
import { Modal, Button, Descriptions, Tag } from 'antd';
import { useQuery } from '@apollo/client';
import { GET_LATEST_BILL_BY_PERIOD_INSTANCE, GET_BILLING_PERIOD_INSTANCE_DETAIL, GET_BILLING_PERIOD_RULE_DETAIL } from '../../graphql/queries/settlement/getSettlementBillQueries';
import { formatTimeWithTimezone } from '../../utils/timezoneFormatter';
import { getBillStatusColor, getBillStatusLabel } from '../../utils/billI18n';

interface BillDetailModalProps {
  visible: boolean;
  periodInstanceId: string | null;
  onClose: () => void;
}

/**
 * 账单详情弹窗组件
 * 根据账期实例ID显示关联的账单详情
 */
export const BillDetailModal: React.FC<BillDetailModalProps> = ({
  visible,
  periodInstanceId,
  onClose
}) => {
  // 查询最新账单
  const { data: latestBillData, loading: billLoading } = useQuery(GET_LATEST_BILL_BY_PERIOD_INSTANCE, {
    variables: { periodInstanceId },
    skip: !periodInstanceId,
    fetchPolicy: 'cache-and-network'
  });

  // 查询账期实例详情（获取规则ID）
  const { data: instanceDetailData, loading: instanceLoading } = useQuery(GET_BILLING_PERIOD_INSTANCE_DETAIL, {
    variables: { instanceId: periodInstanceId ?? '' },
    skip: !periodInstanceId,
    fetchPolicy: 'cache-and-network'
  });

  // 查询账期规则详情（获取规则状态）
  const { data: ruleDetailData, loading: ruleLoading } = useQuery(GET_BILLING_PERIOD_RULE_DETAIL, {
    variables: { ruleId: instanceDetailData?.getBillingPeriodInstanceById?.ruleId ?? '' },
    skip: !instanceDetailData?.getBillingPeriodInstanceById?.ruleId,
    fetchPolicy: 'cache-and-network'
  });

  return (
    <Modal
      title="关联账单详情"
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          关闭
        </Button>
      ]}
      width={800}
    >
      {billLoading || instanceLoading || ruleLoading ? (
        <div className="text-center py-8">加载中...</div>
      ) : latestBillData?.getLatestBillByPeriodInstance ? (
        <div className="space-y-4">
          <Descriptions column={2} bordered>
            <Descriptions.Item label="账单ID" span={2}>
              {latestBillData.getLatestBillByPeriodInstance.id}
            </Descriptions.Item>
            <Descriptions.Item label="账期规则ID">
              {instanceDetailData?.getBillingPeriodInstanceById?.ruleId || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="账期规则说明">
              <Tag color={
                ruleDetailData?.getBillingPeriodRuleById?.status === 1 ? 'green' :
                ruleDetailData?.getBillingPeriodRuleById?.status === 0 ? 'red' : 'default'
              }>
                {ruleDetailData?.getBillingPeriodRuleById?.status === 1 ? '最新规则' :
                 ruleDetailData?.getBillingPeriodRuleById?.status === 0 ? '历史规则' : '未知'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="客户名称">
              {latestBillData.getLatestBillByPeriodInstance.clientName}
            </Descriptions.Item>
            <Descriptions.Item label="账单类型">
              {latestBillData.getLatestBillByPeriodInstance.billType}
            </Descriptions.Item>
            <Descriptions.Item label="账单状态">
              <Tag color={getBillStatusColor(latestBillData.getLatestBillByPeriodInstance.status)}>
                {getBillStatusLabel(latestBillData.getLatestBillByPeriodInstance.status)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="账单金额">
              {latestBillData.getLatestBillByPeriodInstance.billAmount}
            </Descriptions.Item>
            <Descriptions.Item label="账期开始">
              {latestBillData.getLatestBillByPeriodInstance.periodStart && latestBillData.getLatestBillByPeriodInstance.timeZone ? 
                formatTimeWithTimezone(latestBillData.getLatestBillByPeriodInstance.periodStart, latestBillData.getLatestBillByPeriodInstance.timeZone) : 
                latestBillData.getLatestBillByPeriodInstance.periodStart || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="账期结束">
              {latestBillData.getLatestBillByPeriodInstance.periodEnd && latestBillData.getLatestBillByPeriodInstance.timeZone ? 
                formatTimeWithTimezone(latestBillData.getLatestBillByPeriodInstance.periodEnd, latestBillData.getLatestBillByPeriodInstance.timeZone) : 
                latestBillData.getLatestBillByPeriodInstance.periodEnd || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="时区">
              {latestBillData.getLatestBillByPeriodInstance.timeZone}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {latestBillData.getLatestBillByPeriodInstance.createdAt ? 
                new Date(latestBillData.getLatestBillByPeriodInstance.createdAt).toLocaleString('zh-CN') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="更新时间">
              {latestBillData.getLatestBillByPeriodInstance.updatedAt ? 
                new Date(latestBillData.getLatestBillByPeriodInstance.updatedAt).toLocaleString('zh-CN') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="是否可重跑">
              <Tag color={latestBillData.getLatestBillByPeriodInstance.canRerun ? 'green' : 'red'}>
                {latestBillData.getLatestBillByPeriodInstance.canRerun ? '是' : '否'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="是否已作废">
              <Tag color={latestBillData.getLatestBillByPeriodInstance.isCanceled ? 'red' : 'green'}>
                {latestBillData.getLatestBillByPeriodInstance.isCanceled ? '是' : '否'}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          该账期实例暂无关联账单
        </div>
      )}
    </Modal>
  );
};
